import { useEffect, useState } from 'react';
import type { AccountProfile, MeResponse } from '@kassa/shared';
import { api, ApiError } from '../api';
import { useConfirm } from '../components/ConfirmDialog';
import { ChangePassword } from './ChangePassword';
import { SessionsPanel } from './SessionsPanel';

interface Props {
  me: MeResponse;
  onBack: () => void;
  onLogout: () => void;
}

// Google nəticələrinin mesajları (KT-42). Bilinməyən kod üçün ümumi mesaj.
const GOOGLE_NOTICE: Record<string, string> = {
  linked: 'Google hesabı bağlandı.',
  google_email_mismatch: 'Google e-poçtu hesabınızın e-poçtu ilə uyğun deyil.',
  google_linked_elsewhere: 'Bu Google hesabı başqa istifadəçiyə bağlıdır.',
  access_denied: 'Bu Google hesabına hələ icazə verilməyib.',
  google_state: 'Google bağlama vaxtı keçdi. Yenidən cəhd edin.',
  google_token: 'Google girişi təsdiqlənmədi. Yenidən cəhd edin.',
};

// «Hesabım» (KT-42): öz profil, parol və Google bağlantısı. Yalnız öz hesabı.
export function Hesabim({ me, onBack, onLogout }: Props) {
  const confirm = useConfirm();
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [googleOn, setGoogleOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [needGoogle, setNeedGoogle] = useState(false);

  const load = () =>
    api
      .accountProfile()
      .then(setProfile)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Məlumat yüklənmədi'));

  useEffect(() => {
    load();
    fetch('/api/auth/google/enabled', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((b: { enabled: boolean }) => setGoogleOn(b.enabled))
      .catch(() => setGoogleOn(false));
    // Google geri dönüşünün nəticəsi: ?google=<kod>; ünvan təmizlənir
    const code = new URLSearchParams(window.location.search).get('google');
    if (code) {
      setNotice(Object.prototype.hasOwnProperty.call(GOOGLE_NOTICE, code) ? GOOGLE_NOTICE[code] : 'Google ilə bağlama alınmadı.');
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const unlink = async () => {
    if (!profile) return;
    if (!(await confirm('Google hesabı ayrılsın? Bu hesaba Google ilə daxil ola bilməyəcəksiniz (parolla davam edirsiniz).', { confirmLabel: 'Ayır', danger: true }))) return;
    setBusy(true);
    setError(null);
    try {
      await api.googleUnlink();
      setNotice('Google hesabı ayrıldı.');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ayırmaq alınmadı');
    } finally {
      setBusy(false);
    }
  };

  const setInitialPassword = async () => {
    if (newPw.length < 8) return setError('Parol ən azı 8 simvol olmalıdır');
    if (newPw !== newPw2) return setError('Parollar eyni deyil');
    setBusy(true);
    setError(null);
    try {
      await api.passwordInit(newPw);
      setNewPw('');
      setNewPw2('');
      setNeedGoogle(false);
      setNotice('Parol qoyuldu. İndi Google hesabını ayıra bilərsiniz.');
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'google_recent_required') setNeedGoogle(true);
      setError(err instanceof ApiError ? err.message : 'Parol qoyulmadı');
    } finally {
      setBusy(false);
    }
  };

  if (changing) {
    return (
      <ChangePassword
        me={me}
        onDone={() => {
          setChanging(false);
          setNotice('Parol dəyişdi.');
          load();
        }}
        onLogout={onLogout}
        onCancel={() => setChanging(false)}
      />
    );
  }

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn" onClick={onBack}>
          ← Geri
        </button>
        <span className="topbar-spacer" />
        <span className="brand">Hesabım</span>
      </header>
      <main className="page narrow page-fill">
        {notice && <p className="ok" role="status">{notice}</p>}
        {error && <p className="bad" role="alert">{error}</p>}
        {!profile && !error && <p className="muted">Yüklənir…</p>}

        {profile && (
          <section className="card wrap-any" aria-label="Hesab məlumatı">
            <h2>{profile.name}</h2>
            <ul className="product-list">
              {profile.login && <li>Login: <code>{profile.login}</code></li>}
              {profile.email && (
                <li>
                  E-poçt: {profile.email} — {profile.emailVerified ? <span className="ok">təsdiqlənib</span> : <span className="muted">təsdiqlənməyib</span>}
                </li>
              )}
              {profile.phone && <li>Telefon: {profile.phone}</li>}
            </ul>
          </section>
        )}

        <section className="card" aria-label="Parol">
          <h2>Parol</h2>
          {profile && !profile.hasPassword ? (
            <p className="muted small">Parolunuz yoxdur. Parolu «Google hesabı» bölməsində qoyun.</p>
          ) : (
            <button type="button" className="btn-small" onClick={() => setChanging(true)} disabled={busy}>
              Parolu dəyiş
            </button>
          )}
        </section>

        {googleOn && profile && (
          <section className="card wrap-any" aria-label="Google hesabı">
            <h2>Google hesabı</h2>
            {profile.google.linked ? (
              <>
                <p>Bağlıdır: {profile.google.email}</p>
                <button
                  type="button"
                  className="btn-small"
                  onClick={unlink}
                  disabled={busy || !profile.hasPassword}
                >
                  Ayır
                </button>
                {!profile.hasPassword && (
                  <div>
                    <p className="muted small">Parolunuz yoxdur. Ayırmadan əvvəl parol qoyun (Google ilə yenicə daxil olmalısınız).</p>
                    <div className="form-stack">
                      <label className="field">
                        Yeni parol (ən azı 8 simvol)
                        <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" />
                      </label>
                      <label className="field">
                        Yeni parol (təkrar)
                        <input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} autoComplete="new-password" />
                      </label>
                      <button type="button" className="btn-small" onClick={setInitialPassword} disabled={busy || !newPw || !newPw2}>
                        Parol qoy
                      </button>
                    </div>
                    {needGoogle && (
                      <p className="bad small" role="alert">
                        Sessiya köhnədir. <a href="/api/auth/google/start">Google ilə yenidən daxil olun</a>, sonra parol qoyun.
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="muted small">Google ilə daxil olmaq üçün hesabı bağlayın. E-poçt hesabınızın e-poçtu ilə eyni olmalıdır.</p>
                <a className="btn-small" href="/api/auth/google/link">Google-u bağla</a>
              </>
            )}
          </section>
        )}

        <SessionsPanel />
      </main>
    </div>
  );
}
